import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-google-charts';
import Select from 'react-select';
import styled from 'styled-components';

const AlignmentMetersIntro = styled.p`
  max-width: 50rem;
`;

const ChartWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  justify-content: center;
`;

const OptionValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OptionText = styled.span`
  max-width: calc(100% - 4rem);
  overflow: hidden;
`;

const TopBadge = styled.span`
  background-color: #000000;
  color: #ffffff;
  padding: 0.25rem 0.5rem;
  border-radius: 0.625rem;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

const OrganizationSelectContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  margin-block-end: 2rem;

  & .select-table__control {
    border: 1px solid #3d3d3d;
    cursor: pointer;
    border-radius: 0;
  }

  & .select-table__indicator-separator {
    display: none;
  }

  & .select-table__indicator {
    color: #000000;
  }
`;

const OrganizationHeading = styled.h4`
  font-weight: 700;
  line-height: 1.7;
  margin-block: 1rem;
`;

const options = {
  width: 120,
  height: 120,
  redFrom: 0,
  redTo: 40,
  redColor: '#cc6666',
  greenFrom: 60,
  greenTo: 100,
  greenColor: 'lightgreen',
  yellowFrom: 40,
  yellowTo: 60,
  minorTicks: 5,
  format: 'percent',
};

const formatAlignmentData = (alignmentData) => {
  const transformedData = alignmentData.map((item) => {
    return [
      item.last,
      {
        v: Number(item.alignment_score),
        f: `${Number(item.alignment_score).toFixed(1)}%`,
        totalVotes: item.alignment_opp_count,
      },
      item.name,
    ];
  });

  return transformedData;
};

export const AlignmentMeters = ({ alignmentData }) => {
  const transformedAlignmentData = useMemo(
    () => formatAlignmentData(alignmentData),
    [alignmentData]
  );

  // Organization unique organization values
  // Sort alphabetically for select options
  const organizationKeys = useMemo(() => {
    const uniqueNames = new Set();
    alignmentData.forEach((row) => {
      if (row.name) {
        uniqueNames.add(row.name);
      }
    });
    return [...uniqueNames].sort((a, b) => a.localeCompare(b));
  }, [alignmentData]);

  // Full organization data based on unique keys - removes any null values
  const filteredDataByOrgId = useMemo(() => {
    return organizationKeys.map((org) =>
      alignmentData.find((row) => row.name === org)
    );
  }, [alignmentData, organizationKeys]);

  // Sort filtered data by total contribution dollar amount
  const sortedDataByContribution = useMemo(() => {
    return filteredDataByOrgId.sort(
      (a, b) => (Number(b.total_donated) || 0) - (Number(a.total_donated) || 0)
    );
  }, [filteredDataByOrgId]);

  // Default selected value - highest total contribution
  const defaultSelected =
    {
      value: sortedDataByContribution[0]?.name,
      label: (
        <OptionValue>
          <OptionText>{sortedDataByContribution[0]?.name}</OptionText>
          {sortedDataByContribution[0]?.total_donated !== 'N/A' && (
            <TopBadge>Top Donor</TopBadge>
          )}
        </OptionValue>
      ),
    } || '';

  const [selected, setSelected] = useState(defaultSelected);
  const [filteredDatas, setFilteredDatas] = useState([]);

  useEffect(() => {
    const matchingOrganizationKeys = [
      ...transformedAlignmentData.filter(
        (row) => row.includes(selected.value) && !isNaN(row[1].v)
      ),
    ];
    setFilteredDatas(matchingOrganizationKeys);
  }, [selected, transformedAlignmentData]);

  return (
    <div>
      <AlignmentMetersIntro>
        This display is a different look at the “alignments” included in the
        “Legislator-Interest Group Relations” chart above. The drop-down menu
        includes groups that have taken a position on this bill and the gauges
        show the voting alignment between the selected group and each legislator
        on the committee member.
      </AlignmentMetersIntro>
      <div>
        <OrganizationHeading>Organization</OrganizationHeading>
        <OrganizationSelectContainer>
          {organizationKeys.length > 0 ? (
            <Select
              classNamePrefix="select-table"
              defaultValue={selected}
              name="select-table"
              isSearchable={true}
              options={organizationKeys.map((opt) => ({
                value: opt,
                label:
                  opt === defaultSelected.value ? defaultSelected.label : opt,
              }))}
              onChange={(e) => setSelected(e)}
            />
          ) : null}
        </OrganizationSelectContainer>
        {filteredDatas.length > 0 ? (
          <ChartWrapper>
            {filteredDatas.map((data, i) => {
              return (
                <div key={`${data[0]}-${i}`}>
                  <Chart
                    chartType="Gauge"
                    width="100%"
                    height="120px"
                    data={[
                      ['Label', 'Value'],
                      [data[0], data[1]], // [lastName, { v: 33.3, f: '33.3%', totalVotes: '6' }]
                    ]}
                    options={options}
                  />
                </div>
              );
            })}
          </ChartWrapper>
        ) : null}
      </div>
    </div>
  );
};
